import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { UserStatusDisplay } from '@/components/users'
import { User, Workspace } from '@/types'
import { userService, workspaceService } from '@/services'
import { DetailPageLayout, LoadingState, PageHeader } from '@/components/layout'
import { NodeAssociations, NodeAvatar, NodePropertyString } from '@/components/nodes'
import { Boxes, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [user, setUser] = useState<User | null>(null)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const isCurrentUser = currentUser?.id === userId

  useEffect(() => {
    const loadUser = async () => {
      if (!userId) return

      try {
        const userData = await userService.getById(userId)
        setUser(userData)

        // Fetch actual data from services
        const [workspacesData] = await Promise.all([workspaceService.getWorkspacesForUser(userId)])

        setWorkspaces(workspacesData)
      } catch (error) {
        console.error('Error loading user:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUser()
  }, [userId])

  if (isLoading) {
    return <LoadingState variant="spinner" text="Loading user..." />
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="User Not Found"
          backButton={{
            label: 'Back',
            onClick: () => navigate(-1),
          }}
        />
        <p className="text-muted-foreground">User not found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${user.name}'s Profile`}
        prefix={<NodeAvatar node={user} size="xl" />}
        description={<UserStatusDisplay user={user} size="sm" />}
        customActions={
          <div className="flex gap-2">
            {!isCurrentUser && (
              <Button asChild>
                <Link to={`/messages/new?user=${user.id}`}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Message
                </Link>
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to={`/users/${userId}/edit`}>Edit Profile</Link>
            </Button>
          </div>
        }
      />

      <DetailPageLayout>
        <Card>
          <CardHeader>
            <CardTitle>Information</CardTitle>
            <CardDescription>Basic user information and settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NodePropertyString description={user.role} label="Role" textSize="md" />
              <NodePropertyString description={user.email} label="Email" textSize="md" />
              <NodePropertyString description={user.location} label="Location" textSize="md" />
              <NodePropertyString description={user.timezone} label="Timezone" textSize="md" />
              <NodeAssociations
                nodes={workspaces}
                nodeType="workspaces"
                icon={Boxes}
                label="Workspaces:"
              />
            </div>
          </CardContent>
        </Card>
      </DetailPageLayout>
    </div>
  )
}
